import React, { useCallback, useMemo, useState } from 'react';

import { api } from 'services/api';
import { isAxiosError } from 'services/axios';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useMediaQuery,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import { prepareCompositionApiPayload } from 'pages/Composition/Add/shared/prepareCompositionPayload';
import Identification from 'pages/Composition/Add/SubPages/Identification';
import { CompositionIdentification } from 'pages/Composition/Add/SubPages/Identification/Form';
import PriceByGroup from 'pages/Composition/Add/SubPages/PriceByGroup';
import { CompositionResource } from 'pages/Composition/Add/types';

import { Budget } from 'types/budget';

type BudgetBaseWithModelType = Budget['bases'][number] & {
  version: Budget['bases'][number]['version'] & {
    base: Budget['bases'][number]['version']['base'] & {
      model_type?: string;
    };
  };
};

type ModalData = {
  budgetId: number;
  subStageId: number;
  bases: Budget['bases'];
};

interface ModalSiengeBudgetCustomCompositionAdd extends ModalRootProps {
  data: ModalData;
}

const modalMargin = 120;
const modalMobileFix = 100;

const SiengeBudgetCustomCompositionAdd: React.FC<ModalSiengeBudgetCustomCompositionAdd> =
  ({
    data: { budgetId, subStageId, bases },
    handleClose,
    onConfirm,
    ...restProps
  }) => {
    const [isMobile] = useMediaQuery('(max-width: 728px)');
    const [activeTabKey, setActiveTabKey] = useState<
      'identification' | 'price'
    >('identification');
    const [saving, setSaving] = useState(false);
    const [identificationData, setIdentificationData] =
      useState<CompositionIdentification | null>(null);
    const [priceSpecificitiesDraft, setPriceSpecificitiesDraft] = useState<
      Array<{
        referenceId: string;
        description: string;
        category: string;
        total: number;
      }>
    >([]);

    const validSiengeBudgetBases = useMemo(() => {
      return (bases || []).filter((rawBudgetBase) => {
        const budgetBase = rawBudgetBase as BudgetBaseWithModelType;
        return budgetBase.version.base.model_type === 'sienge';
      });
    }, [bases]);

    const initialData = useMemo(() => {
      const firstBudgetBase = validSiengeBudgetBases[0] as
        | BudgetBaseWithModelType
        | undefined;

      if (validSiengeBudgetBases.length === 1 && firstBudgetBase) {
        return {
          base_id: firstBudgetBase.version.base.id,
          base_model_type: 'sienge',
          version_id: firstBudgetBase.version.id,
          kind: 'informed',
          quantity: 1,
        } as CompositionIdentification;
      }

      return {
        kind: 'informed',
        quantity: 1,
      } as CompositionIdentification;
    }, [validSiengeBudgetBases]);

    const activeTabIndex = activeTabKey === 'identification' ? 0 : 1;

    const handleSubmitIdentification = useCallback(
      (data: CompositionIdentification) => {
        setIdentificationData(data);
        setActiveTabKey('price');
      },
      [],
    );

    const handleSubmitAndAddToBudget = useCallback(
      async (
        priceSpecificity: Array<{
          referenceId: string;
          description: string;
          category: string;
          total: number;
        }>,
      ) => {
        if (!identificationData) {
          toast({
            description: 'Preencha os dados de identificação.',
            status: 'error',
          });
          return;
        }

        const budgetBase = validSiengeBudgetBases.find(
          (item) => item.version.base.id === Number(identificationData.base_id),
        );

        if (!budgetBase) {
          toast({
            description: 'Selecione uma base válida do orçamento.',
            status: 'error',
          });
          return;
        }

        setSaving(true);
        const processingToastId = toast({
          description:
            'Serviço a preço informado está sendo criado e adicionado ao orçamento. Aguarde...',
          status: 'info',
          duration: null,
          isClosable: false,
        });

        try {
          const { apiPayload } = prepareCompositionApiPayload({
            payload: {
              ...identificationData,
              kind: 'informed',
            },
            fallbackBaseModelType: 'sienge',
          });

          const {
            data: {
              data: { id: compositionId },
            },
          } = await api.post('/composition', {
            ...apiPayload,
          });

          const compositionResponse = await api.get(
            `/composition/${compositionId}`,
          );
          const compositionLoaded: CompositionResource =
            compositionResponse.data.data;
          const firstPrice = compositionLoaded.prices?.[0];

          if (!firstPrice?.id) {
            toast({
              description:
                'Não foi possível carregar o preço do serviço criado.',
              status: 'error',
            });
            return;
          }

          await api.put(
            `/composition/${compositionId}/price/${firstPrice.id}`,
            {
              price_specificity: priceSpecificity,
            },
          );

          await api.post(`budget/${budgetId}/component`, {
            parent_stage_id: subStageId,
            components: [
              {
                type: 'composition_original',
                composition_id: compositionId,
                quantity: Number(identificationData.quantity) || 0,
                budget_base_id: budgetBase.id,
              },
            ],
          });

          toast({
            description: 'Serviço a preço informado adicionado com sucesso!',
            status: 'success',
          });

          onConfirm?.();
          handleClose();
        } catch (err) {
          if (isAxiosError(err)) {
            toast({
              description:
                err.response?.data?.message ||
                'Houve um erro ao adicionar serviço a preço informado.',
              status: 'error',
            });
            return;
          }

          toast({
            description:
              'Houve um erro ao adicionar serviço a preço informado.',
            status: 'error',
          });
        } finally {
          if (processingToastId) {
            toast.close(processingToastId);
          }
          setSaving(false);
        }
      },
      [
        budgetId,
        handleClose,
        identificationData,
        onConfirm,
        subStageId,
        validSiengeBudgetBases,
      ],
    );

    return (
      <Modal {...restProps} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent
          sx={{
            width: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            maxWidth: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            height: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
            maxHeight: `calc(100% - ${
              isMobile ? modalMargin - modalMobileFix : modalMargin
            }px)`,
          }}
        >
          <ModalHeader>Adicionar serviço a preço informado</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Tabs
              variant="solid-rounded"
              colorScheme="red"
              index={activeTabIndex}
              onChange={(index) => {
                setActiveTabKey(index === 0 ? 'identification' : 'price');
              }}
              align={isMobile ? 'center' : 'start'}
              size={isMobile ? 'sm' : 'md'}
              isLazy
              position="relative"
            >
              <TabList>
                <Tab borderRadius="8px">Identificação</Tab>
                <Tab
                  borderRadius="8px"
                  isDisabled={!identificationData}
                  _disabled={{ cursor: 'not-allowed', color: 'gray.500' }}
                >
                  Preço por grupo de insumo
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={0} pt={4}>
                  <Identification
                    initialData={identificationData || initialData}
                    isEditing={false}
                    isFromModal
                    saving={saving}
                    loading={false}
                    onSubmit={handleSubmitIdentification}
                    budgetBaseOptions={validSiengeBudgetBases}
                    isKindLocked
                    fixedKind="informed"
                    hideKindField
                    disableBaseVersionWhenInitialData={false}
                    showQuantityField
                  />
                </TabPanel>
                <TabPanel p={0} pt={4}>
                  <PriceByGroup
                    composition={{
                      prices: [
                        {
                          price_specificity: priceSpecificitiesDraft,
                        },
                      ],
                    }}
                    isLoading={false}
                    isFromModal
                    onSpecificitiesChange={setPriceSpecificitiesDraft}
                    onSubmitSpecificities={handleSubmitAndAddToBudget}
                    submitButtonLabel="Salvar e adicionar"
                    showRefreshButton={false}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

export default SiengeBudgetCustomCompositionAdd;
